/**
 * RequireAdmin — Guard für Admin-only Routen.
 *
 * Verkettet sich mit RequireAuth: dort wird Login geprüft, hier zusätzlich
 * die Rolle. Wenn der eingeloggte User kein Admin ist, wird mit Hinweis
 * auf /cases umgeleitet.
 *
 * Sicherheits-Hinweis: dies ist UI-Convenience. Der eigentliche Schutz
 * liegt in den RLS-Policies (Migration 0024) — selbst wenn der User
 * /admin/settings direkt aufruft und der Guard verbuggt wäre, würde der
 * Versuch, einen Flag zu setzen, von Postgres abgelehnt.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthOrSession } from '../../lib/authOrSession';
import { useUserRole } from '../../lib/useUserRole';

interface RequireAdminProps {
  children: ReactNode;
}

export function RequireAdmin({ children }: RequireAdminProps) {
  const { user, isLoading: authLoading } = useAuthOrSession();
  const { isAdmin, isLoading: roleLoading } = useUserRole();

  // Während Auth- oder Rolle-Hydration: leerer Frame (kein Flicker).
  if (authLoading || roleLoading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    // Kein Crash, kein Toast — Redirect mit URL-Marker, damit Cases-Seite
    // optional einen kurzen Hinweis zeigen kann ("Diese Seite ist nur für
    // Admin-Konten zugänglich"). Hier reicht der saubere Redirect.
    return <Navigate to="/cases?denied=admin" replace />;
  }

  return <>{children}</>;
}
